import { useEffect, useMemo, useRef, useState } from "react";

const WATCHDOG_MS = 4000;

const canAnimate = () => {
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return false;
  try {
    const c = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && (c.getContext("webgl2") || c.getContext("webgl")));
  } catch (e) {
    return false;
  }
};

// The 3D scene renders the travelling title; this hook only owns the DOM h1 hand-off state.
export const useTitleIntro = () => {
  const titleRef = useRef(null);
  const [phase, setPhase] = useState(() => (canAnimate() ? "pending" : "done"));
  const landed = useRef(phase === "done");
  const lastFrame = useRef(0);

  const intro = useMemo(() => {
    const land = () => {
      if (landed.current) return;
      landed.current = true;
      setPhase("done");
    };
    return {
      titleEl: () => titleRef.current,
      isLanded: () => landed.current,
      onFrame: () => { lastFrame.current = performance.now(); },
      onHandoff: land,
      onFallback: land,
      land,
    };
  }, []);

  // Land the title if intro frames stop arriving (scene never started, tab hidden, hero scrolled away).
  useEffect(() => {
    if (landed.current) return undefined;
    lastFrame.current = performance.now() + 3000;
    const id = window.setInterval(() => {
      if (landed.current) window.clearInterval(id);
      else if (performance.now() - lastFrame.current > WATCHDOG_MS) intro.land();
    }, 400);
    return () => window.clearInterval(id);
  }, [intro]);

  return { titleRef, phase, intro };
};
